import type { MockupTemplate } from '../types';

type PreviewCacheRecord = {
  id: string;
  blob: Blob;
  hash: string;
  previewUrl: string;
};

const DB_NAME = 'mockup-use-plugin-preview-cache';
const DB_VERSION = 1;
const STORE_NAME = 'preview-cache';

export async function loadCachedPreviewUrls(templates: MockupTemplate[]): Promise<Record<string, string>> {
  const db = await openDatabase();
  if (!db) {
    return {};
  }

  const result: Record<string, string> = {};

  for (const template of templates) {
    const record = await getRecord(db, template.id);
    if (!record || record.previewUrl !== template.preview) {
      continue;
    }

    const serverToken = getServerPreviewToken(template);
    if (serverToken && record.hash !== serverToken) {
      continue;
    }

    result[template.id] = URL.createObjectURL(record.blob);
  }

  return result;
}

export async function syncPreviewCache(templates: MockupTemplate[]): Promise<Record<string, string>> {
  const db = await openDatabase();
  if (!db) {
    return {};
  }

  const updatedUrls: Record<string, string> = {};
  const validIds = new Set(templates.map((template) => template.id));

  for (const template of templates) {
    try {
      const currentRecord = await getRecord(db, template.id);
      const serverToken = getServerPreviewToken(template);

      if (currentRecord && currentRecord.previewUrl === template.preview && serverToken && currentRecord.hash === serverToken) {
        continue;
      }

      const response = await fetch(template.preview, { cache: 'no-store' });
      if (!response.ok) {
        continue;
      }

      const blob = await response.blob();
      const nextHash = serverToken || (await hashBlob(blob));

      if (currentRecord && currentRecord.hash === nextHash && currentRecord.previewUrl === template.preview) {
        continue;
      }

      await putRecord(db, {
        id: template.id,
        blob,
        hash: nextHash,
        previewUrl: template.preview
      });

      updatedUrls[template.id] = URL.createObjectURL(blob);
    } catch (error) {
      console.warn('[preview-cache] sync failed', template.id, error);
    }
  }

  try {
    const keys = await getAllKeys(db);
    for (const key of keys) {
      const id = String(key);
      if (!validIds.has(id)) {
        await deleteRecord(db, id);
      }
    }
  } catch (error) {
    console.warn('[preview-cache] cleanup failed', error);
  }

  return updatedUrls;
}

function getServerPreviewToken(template: MockupTemplate): string {
  if (template.previewHash) {
    return `hash:${template.previewHash}`;
  }

  if (template.previewUpdatedAt) {
    return `mtime:${template.previewUpdatedAt}`;
  }

  return '';
}

async function openDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') {
    return null;
  }

  try {
    return await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error('IndexedDB open failed'));
    });
  } catch (error) {
    console.warn('[preview-cache] indexedDB unavailable', error);
    return null;
  }
}

async function getRecord(db: IDBDatabase, id: string): Promise<PreviewCacheRecord | null> {
  try {
    const value = await runRequest<PreviewCacheRecord | undefined>(
      db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(id)
    );
    return value || null;
  } catch {
    return null;
  }
}

async function putRecord(db: IDBDatabase, record: PreviewCacheRecord): Promise<void> {
  const transaction = db.transaction(STORE_NAME, 'readwrite');
  transaction.objectStore(STORE_NAME).put(record);
  await waitForTransaction(transaction);
}

async function deleteRecord(db: IDBDatabase, id: string): Promise<void> {
  const transaction = db.transaction(STORE_NAME, 'readwrite');
  transaction.objectStore(STORE_NAME).delete(id);
  await waitForTransaction(transaction);
}

async function getAllKeys(db: IDBDatabase): Promise<IDBValidKey[]> {
  return runRequest<IDBValidKey[]>(db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAllKeys());
}

function runRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
  });
}

function waitForTransaction(transaction: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'));
    transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
  });
}

async function hashBlob(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let hash = 2166136261;

  for (let index = 0; index < bytes.length; index += 1) {
    hash ^= bytes[index];
    hash = Math.imul(hash, 16777619);
  }

  return `${blob.size}:${(hash >>> 0).toString(16)}`;
}
